import { useEffect, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import Head from 'components/Header/Head';
import Header from 'components/Header/Header';
import ArticleNav from 'components/Post/Article/ArticleNav';
import ArticleBody from 'components/Post/Article/ArticleBody';
import RouteFlourish from 'components/Post/Article/RouteFlourish';
import Footer from 'components/General/Footer';
import { getUser } from 'helpers/token';
import Image from 'components/FutureImage';
import { fraunces } from 'components/Landing/fonts';
import {
  ArrowIcon,
  CtaButton,
  Eyebrow,
  GhostButton,
  INK,
  MUTED,
  PAPER_CARD,
} from 'components/Landing/ui';
import cover from '../../public/img/start-blog/cover.jpg';
import tfLanding from '../../public/img/blog-platforms/travelfeed.jpg';
import tfCommunity from '../../public/img/blog-platforms/travelfeed-community.jpg';

// Signup CTAs open the boarding-pass JoinDialog (via LoginButton), never
// the legacy /join page; lazy so the dialog stack stays out of the bundle.
const LoginButton = dynamic(
  () => import('components/Header/LoginButton'),
  { ssr: false },
);

const CANONICAL = 'https://travelfeed.com/start-a-travel-blog';
const COVER_URL = 'https://travelfeed.com/img/start-blog/cover.jpg';

// Answers double as FAQPage JSON-LD (via Head) — schema content must match
// the visible page, so both render from this one array.
const FAQ = [
  {
    questionName: "What should I budget for a travel blog's first year?",
    acceptedAnswerText:
      'Between $0 and about $300 for the first year, depending on the route. Free platforms cost nothing but keep the address and may show their own ads. A hosted travel blog on TravelFeed is $150 in year one on the Pro plan (a 50% new-member discount applies automatically at checkout; Basic is $180/yr) plus a domain; website builders like Squarespace and Wix land around $190–205; self-hosted WordPress looks cheaper in year one at intro pricing and usually costs more from year two. The only near-universal cost is the domain: $10–15 a year.',
  },
  {
    questionName: 'Can I start a travel blog for free?',
    acceptedAnswerText:
      "Yes — Blogger, WordPress.com's free tier, Substack and Travellerspoint all host writing at no cost. The tradeoffs: you publish on the platform's subdomain, some free tiers show ads you don't earn from, and moving to your own domain later means largely starting over with search engines. Since a year of blogging costs far more in hours than in dollars, weigh time over money: TravelFeed's $19-a-year Starter membership runs the same five-post experiment in front of real readers, no hosting plan required, and upgrading to your own domain later keeps everything.",
  },
  {
    questionName: 'Do I need to know how to code?',
    acceptedAnswerText:
      "No. On TravelFeed, Squarespace, Wix or WordPress.com there is no code anywhere in the process. Self-hosted WordPress doesn't require code either, but it does make you the site's part-time administrator — updates, backups, security — which is the part people mistake for needing to be technical.",
  },
  {
    questionName: 'Do I need WordPress to start a travel blog?',
    acceptedAnswerText:
      "No. WordPress is capable software that runs roughly 40% of the web, but for a travel blog it's one option, not a requirement — and it's the option that makes you responsible for maintenance. Choose it if you want full control and enjoy tinkering; skip it without guilt if you just want to write.",
  },
  {
    questionName: 'How long until Google sends my blog readers?',
    acceptedAnswerText:
      "Months. Three to six months for a new blog's posts to start ranking is normal, longer for competitive topics. That's why the first-readers plan shouldn't depend on search: tell people you know, publish into communities, and let search compound in the background. Specific, first-person posts about exact places and routes rank soonest.",
  },
  {
    questionName: 'Is it too late to start a travel blog in 2026?',
    acceptedAnswerText:
      "It's a bad time to start a generic one — a list of the top ten things to do in Lisbon now competes with every AI answer box. It's a fine time to start a specific one. First-person experience — what a border crossing was actually like last month, what the guesthouse actually cost — is what generic answers can't provide, what readers trust, and what search engines increasingly reward.",
  },
];

function StepLabel({ children }) {
  return (
    <p className="tw-mb-0 tw-mt-16 tw-font-mono tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-primary-700 dark:tw-text-primary-300">
      {children}
    </p>
  );
}

// h2 directly after a StepLabel: cancel tf-article's top margin so the
// label + heading read as one unit.
function StepHeading({ id, children }) {
  return (
    <h2 id={id} className="tw-mt-2">
      {children}
    </h2>
  );
}

// Honest guide to starting a travel blog — the pillar page of the
// blogger-intent SEO cluster (docs/seo-plan.md §3). First-person founder
// voice for E-E-A-T; links every cluster page.
export default function StartBlogGuide() {
  const [isMember, setIsMember] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    if (getUser()) setIsMember(true);
  }, []);

  // Warm paper behind everything — SSR first paint comes from the
  // PAPER_BODY_ROUTES entry in _document.js.
  useEffect(() => {
    document.body.classList.add('tf-paper');
    return () => document.body.classList.remove('tf-paper');
  }, []);

  return (
    <>
      <Head
        shorttitle="How to Start a Travel Blog in 2026: The Honest Guide"
        description="Audience, name, platform, costs — how to start a travel blog in 2026, in the order that matters. An honest guide from TravelFeed's founder, including when you don't need WordPress and what free really costs."
        seo={{ canonicalUrl: CANONICAL }}
        image={`https://og.travelfeed.com/api/post?title=${encodeURIComponent(
          'How to Start a Travel Blog in 2026: The Honest Guide',
        )}&image=${encodeURIComponent(COVER_URL)}`}
        post={{
          author: { username: 'jpphotography', displayName: 'Julian' },
          createdAt: '2026-07-16',
          updatedAt: '2026-07-16',
          tags: ['travel-blogging'],
          thumbnail: COVER_URL,
        }}
        faq={FAQ}
      />
      {isMember ? <Header /> : <ArticleNav />}
      <div className="tw-pt-20" />

      <header className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-4 tw-pt-8 sm:tw-pt-12">
        <Eyebrow>Field guide · Updated July 2026</Eyebrow>
        <h1
          className={`${fraunces.className} tw-m-0 tw-text-4xl tw-font-semibold tw-leading-[1.08] tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] sm:tw-text-5xl lg:tw-text-[3.4rem]`}
        >
          How to start a travel blog
        </h1>
        <p className={`${MUTED} tw-mb-0 tw-mt-5 tw-text-lg tw-leading-relaxed`}>
          Audience, name, platform, money — the eight decisions that matter, in
          the order that matters. No code required, and the only thing this
          guide insists you buy is a $12 domain.
        </p>
        <p className="tw-mb-0 tw-mt-5 tw-font-mono tw-text-[11px] tw-uppercase tw-tracking-[0.18em] tw-text-[#8A958F]">
          By{' '}
          <Link
            href="/@jpphotography"
            className="tw-text-primary-700 dark:tw-text-primary-300 hover:tw-underline"
          >
            Julian
          </Link>
          , founder of TravelFeed · 8 steps · Bias disclosed
        </p>
      </header>

      {/* Postcard-framed cover, shared 64rem hero width */}
      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-pb-6 tw-pt-4">
        <div className="tw-rounded-[4px] tw-bg-white dark:tw-bg-[#EFEAE0] tw-p-3 tw-shadow-[0_30px_60px_-20px_rgba(29,45,40,0.35)]">
          <Image
            src={cover}
            alt="Painted flat-lay of a travel-blog planning desk: an open journal, a route map with waypoint flags, a brass compass, luggage tags and a postcard-sized browser window"
            priority
            sizes="(max-width: 1024px) 92vw, 990px"
            className="tw-h-auto tw-w-full tw-rounded-[2px]"
          />
        </div>
      </div>

      <ArticleBody>
        <p>
          Starting a travel blog takes an afternoon. Starting one you&rsquo;ll
          still be updating in two years takes about eight decisions — and most
          people make them in the wrong order, buying hosting and a domain
          before they know who the blog is for.
        </p>
        <p>
          Here&rsquo;s my bias, stated plainly: I&rsquo;m Julian, I founded
          TravelFeed, and TravelFeed sells hosted travel blogs. Nearly every
          other guide ranking for this search is monetized too — mostly through
          hosting referrals that pay $65 or more per signup, which is why they
          all conclude that you need to buy hosting. I&rsquo;d rather be the
          guide that tells you when you don&rsquo;t.
        </p>

        <aside
          className={`${PAPER_CARD} tw-my-10 tw-rounded-2xl tw-border-dashed tw-p-6 sm:tw-p-8 [border-width:1.5px]`}
        >
          <p className="tw-mb-2 tw-mt-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.22em] tw-text-[#8A958F]">
            Why this guide exists
          </p>
          <p className={`${INK} tw-mb-0 tw-text-[0.95em] tw-leading-relaxed`}>
            TravelFeed started in 2018 as a community of travel writers. The
            hosting side came later, and it began the way these stories
            usually do: a plugin update broke my girlfriend&rsquo;s
            never-fast WordPress travel blog. She asked her host — Bluehost,
            at the time — for help, and by the end of the support session
            the site was past saving. That was one experience, years ago, and plenty of blogs
            live there happily. But it settled something for me: the person
            doing the writing shouldn&rsquo;t also have to be the person
            doing the server rescue. I built TravelFeed&rsquo;s hosting so
            nobody we host ever has that afternoon — and that&rsquo;s the
            bias running through this guide.
          </p>
        </aside>

        <StepLabel>Step 01 · The audience</StepLabel>
        <StepHeading id="who-is-it-for">
          Decide who the blog is for
        </StepHeading>
        <p>
          Not the niche — the audience. Every other decision gets easier once
          you know which of these three blogs you&rsquo;re starting:
        </p>
        <ul>
          <li>
            <strong>A blog for friends and family.</strong> You&rsquo;re going
            traveling, and the people who love you want to follow along without
            a group chat drowning in photos. You don&rsquo;t need a niche, a
            content calendar or SEO — you need something easy to update from a
            phone and pleasant to read on one. If this is you, our{' '}
            <Link href="/blog/how-to-make-a-travel-blog-for-friends-and-family">
              guide to making a travel blog for friends and family
            </Link>{' '}
            is written for exactly this, and you can skip the ambition sections
            below.
          </li>
          <li>
            <strong>A blog for strangers.</strong> Readers you&rsquo;ve never
            met, maybe income eventually. This is the version the rest of this
            guide takes seriously: it adds a real name decision, your own
            domain, a platform that search engines index properly, and patience
            measured in months.
          </li>
          <li>
            <strong>A journal for yourself.</strong> No audience at all — you
            want to remember the trip. A private blog works, and so does a
            tracking app like Polarsteps. The difference matters later:{' '}
            <Link href="/polarsteps-alternative">
              a journal that lives inside an app isn&rsquo;t a website
            </Link>
            , and if yours might ever become one, keep reading.
          </li>
        </ul>
        <p>
          Most advice online assumes the second blog. Be honest with yourself
          if you&rsquo;re starting the first or third — you get to skip half
          the work.
        </p>

        <StepLabel>Step 02 · The name</StepLabel>
        <StepHeading id="pick-a-name">
          Pick a name you won&rsquo;t outgrow
        </StepHeading>
        <p>
          Names that trap people share a pattern: they describe a temporary
          situation. An age, a relationship, a vehicle, the one country
          you&rsquo;re excited about this year — any of them can expire while
          the blog is still going strong. Pick something roomier: an image, a
          feeling, a way of traveling.
        </p>
        <p>
          Test your shortlist the unglamorous way: say each name out loud,
          spell it from memory, and read it as a domain with the spaces
          removed. Then check that the name and the .com are actually
          available. We built a{' '}
          <Link href="/tools/travel-blog-name-ideas-generator">
            free travel blog name generator
          </Link>{' '}
          that checks domain and username availability in one click, and
          collected{' '}
          <Link href="/blog/travel-blog-name-ideas">
            300+ travel blog name ideas
          </Link>{' '}
          sorted by niche if you&rsquo;d rather browse than generate. Ten
          minutes there beats renaming a two-year-old blog later.
        </p>

        <StepLabel>Step 03 · The platform</StepLabel>
        <StepHeading id="choose-a-platform">
          Choose where the blog lives
        </StepHeading>
        <p>
          This is the decision with the most noise around it, so here is the
          short, honest version. A travel blog can live in four kinds of
          places:
        </p>
        <p>
          <strong>Self-hosted WordPress.</strong> You rent a server, install
          WordPress, and own every layer. Maximum control, maximum
          maintenance: updates, backups, security and speed are your job,
          forever. It looks cheap in year one because hosts sell at intro
          prices; renewals typically cost two to five times more.
        </p>
        <p>
          <strong>Website builders</strong> (Squarespace, Wix). Drag-and-drop
          sites with polished templates, around $16–17 a month billed yearly.
          Nothing about them is built for travel, though: no maps, no trip
          structure, no travel readership. You&rsquo;re building a
          general-purpose website that happens to contain travel posts.
        </p>
        <p>
          <strong>Hosted blog platforms</strong> — that&rsquo;s us, and also
          Ghost, Substack and others. The platform runs the servers; you
          write. You trade some control for never thinking about maintenance.
          TravelFeed is the travel-specific one: your posts geotag onto a map,
          and that pin is what builds your country, region and city pages for
          you. Your blog runs on our hosting with your own domain, and you
          publish into an existing community of travel readers instead of into
          the void.
        </p>
        <p>
          <strong>Tracking apps</strong> (Polarsteps and friends). Beautiful
          automatic trip logs, and genuinely great at what they do. But an app
          profile isn&rsquo;t a website: no domain of yours, no real presence
          on Google, and the archive lives on the app&rsquo;s terms. If
          you&rsquo;re weighing a tracker against a blog, we wrote an honest
          head-to-head:{' '}
          <Link href="/polarsteps-alternative">
            Polarsteps vs. a real travel blog
          </Link>
          .
        </p>
        <p>
          We also compared the ten platforms people actually shortlist —
          including the matchups where TravelFeed loses — in{' '}
          <Link href="/blog/best-travel-blog-platforms">
            the best travel blog platforms in 2026
          </Link>
          .
        </p>
        <h3 id="do-you-need-wordpress">Do you need WordPress?</h3>
        <p>
          No — and I say that as someone who ran WordPress blogs for years
          before building an alternative. WordPress powers roughly 40% of the
          web and earned it: capable, open-source, endlessly extensible. But
          &ldquo;do I need it&rdquo; has a simple answer: only if you want the
          tinkering. A WordPress site makes you its part-time administrator —
          plugin updates, backup checks, security patches, speed tuning. Some
          people genuinely enjoy that; I did, until I didn&rsquo;t. If you
          don&rsquo;t, you can run an excellent travel blog in 2026 without
          ever seeing a plugin screen. The detailed comparison:{' '}
          <Link href="/blog/wordpress-vs-travelfeed-the-best-choice-for-travel-bloggers-2023">
            WordPress vs. TravelFeed
          </Link>
          . Already on WordPress and tired of it? The exit is mapped in{' '}
          <Link href="/leaving-wordpress">leaving WordPress</Link>.
        </p>

        <StepLabel>Step 04 · The free question</StepLabel>
        <StepHeading id="start-a-travel-blog-for-free">
          Can you start a travel blog for free?
        </StepHeading>
        <p>
          Yes. Blogger and WordPress.com&rsquo;s free tier will host your
          writing at no cost, Substack is free if your blog is really a
          newsletter, and Travellerspoint has offered free travel blogs since
          the 2000s. TravelFeed is not free — membership starts at $19 a year —
          and if the budget is genuinely zero, those are the options.
        </p>
        <p>
          What free costs you instead: the address is theirs
          (yourname.blogspot.com), WordPress.com&rsquo;s free tier shows its
          own ads on your posts, and the platform&rsquo;s subdomain is the one
          part you can&rsquo;t take with you — outgrowing it usually means a
          new address and starting over with search engines.
        </p>
        <p>
          My honest advice: run the math on what you&rsquo;re actually
          investing. A first year of blogging is fifty-plus hours of writing;
          against that, $19 is a rounding error — less than the domain
          itself. What the free route really costs is where those hours land:
          a subdomain you&rsquo;ll abandon, ads you don&rsquo;t earn from, and
          an audience of nobody. The five-post experiment is still the right
          test of whether you&rsquo;ll keep writing — I&rsquo;d just run it on
          a $19 Starter membership instead, with no hosting plan required:
          your posts publish in front of a community that reads and comments
          from day one, and if the experiment succeeds, upgrading to your own
          domain happens in place. Nothing starts over.
        </p>

        <StepLabel>Step 05 · The address</StepLabel>
        <StepHeading id="buy-a-domain">
          Buy the domain — the only must-spend
        </StepHeading>
        <p>
          A .com runs $10–15 a year at any honest registrar, and it&rsquo;s the
          only purchase this guide insists on once you&rsquo;re past the
          experiment stage — because it&rsquo;s the only part of a blog you
          truly own. Platforms come and go; a domain moves with you, and its
          search history moves with it.
        </p>
        <p>
          Keep it boring: the .com if you can get it, no hyphens, no clever
          spellings people need to see written down. WHOIS privacy is free at
          any registrar worth using. And you need exactly one domain — the
          matching .net and .org will not be missed. Every serious platform,
          WordPress and builders and TravelFeed alike, connects your own
          domain in settings.
        </p>

        <StepLabel>Step 06 · The setup</StepLabel>
        <StepHeading id="set-up-the-blog">Set the blog up</StepHeading>
        <p>How long this takes is entirely a platform decision:</p>
        <ul>
          <li>
            <strong>WordPress:</strong> a hosting account, the WordPress
            install, a theme you&rsquo;ll audition three of, and six to ten
            plugins for the things blogs need — backups, caching, SEO, spam
            filtering, image compression. An evening, if everything
            cooperates.
          </li>
          <li>
            <strong>A website builder:</strong> pick a template, drag things
            around until it looks right. An afternoon.
          </li>
          <li>
            <strong>TravelFeed:</strong> sign up, pick a design, connect your
            domain. Minutes — blog plans start at $15 a month billed yearly,
            and the $19-a-year Starter membership publishes on travelfeed.com
            if you don&rsquo;t need your own site yet.
          </li>
        </ul>
        <figure className="tf-wide">
          <Image
            src={tfLanding}
            alt="TravelFeed — hosted travel blogs on your own domain, set up in minutes"
            sizes="(max-width: 640px) 100vw, (max-width: 1088px) 92vw, 1024px"
            className="tw-h-auto tw-w-full"
          />
          <figcaption>
            The direct-flight route: a hosted travel blog, no plugins to
            babysit
          </figcaption>
        </figure>
        <p>
          One warning that applies everywhere: don&rsquo;t redesign. Readers
          subscribe to writing, not layouts, and a redesign is the most
          productive-feeling way to not write. Pick something readable and go.
        </p>

        <StepLabel>Step 07 · The writing</StepLabel>
        <StepHeading id="write-your-first-posts">
          Write the first three posts
        </StepHeading>
        <p>
          Skip the &ldquo;welcome to my blog&rdquo; post. Nobody searches for
          it, and the readers who arrive later won&rsquo;t read it. Start with
          the story you already tell in person — the border crossing that went
          sideways, the meal that justified the whole detour, the town you
          almost skipped. If you&rsquo;ve told it out loud twice, it&rsquo;s a
          blog post.
        </p>
        <p>
          <strong>Specifics beat superlatives.</strong> &ldquo;The 9:30 train
          from Kandy left an hour late, which by hill-country standards meant
          on time&rdquo; puts a reader on the platform. &ldquo;Amazing hill
          country views&rdquo; puts them to sleep. Names, numbers, times,
          prices — details are what make travel writing feel like travel.
        </p>
        <p>
          <strong>Ten photos, not sixty.</strong> Photo dumps read as
          homework. Pick the frames that carry the story and cut the rest —
          a gallery is a highlight reel, not evidence.
        </p>
        <p>
          Three posts is the threshold worth hitting before you tell anyone
          the blog exists. One post is an experiment. Three posts is a blog.
        </p>
        <p>
          And if you want to see what good looks like before you write a
          word, we collected{' '}
          <Link href="/blog/travel-blog-examples">
            four real travel blog examples worth studying
          </Link>{' '}
          — what each one gets right, and what&rsquo;s worth stealing.
        </p>

        <StepLabel>Step 08 · The readers</StepLabel>
        <StepHeading id="find-your-first-readers">
          Find your first readers
        </StepHeading>
        <p>
          The honest sequencing: people you know, then communities, then
          search — because that&rsquo;s the order they arrive in.
        </p>
        <p>
          <strong>Tell actual humans.</strong> Your first fifty readers are
          people who already like you. Send them the link. This feels
          embarrassing and works better than everything else combined in month
          one.
        </p>
        <p>
          <strong>Publish where readers already are.</strong> A brand-new
          standalone site starts at zero; publishing into an existing
          community doesn&rsquo;t. That&rsquo;s the structural head start
          TravelFeed blogs get — every post also reaches the TravelFeed
          community feed and curation from day one — but the principle is
          general: forums, Reddit and Facebook groups in your niche all beat
          shouting into the void.
        </p>
        <figure className="tf-wide">
          <Image
            src={tfCommunity}
            alt="A TravelFeed member profile with passport stamps for visited countries and a grid of travel stories"
            sizes="(max-width: 640px) 100vw, (max-width: 1088px) 92vw, 1024px"
            className="tw-h-auto tw-w-full"
          />
          <figcaption>
            Readers from day one: a member profile on TravelFeed
          </figcaption>
        </figure>
        <p>
          <strong>Let search compound quietly.</strong> SEO for a new blog is
          measured in months — three to six before Google takes a page
          seriously is normal. Write for the specific search you can win
          (&ldquo;crossing the Kazakh–Uzbek border by shared taxi&rdquo;), not
          the one you can&rsquo;t (&ldquo;things to do in Paris&rdquo;), and
          treat every post as a small asset that appreciates.
        </p>
        <p>
          <strong>Don&rsquo;t build the house on Instagram.</strong> Social
          accounts grow a following you don&rsquo;t control on land you
          don&rsquo;t own — one algorithm change can hide you from your own
          followers overnight. Post there to point people home, not instead of
          having a home. If Instagram is where your audience already is, we
          wrote about{' '}
          <Link href="/blog/how-to-start-a-travel-blog-on-instagram">
            starting a travel blog on Instagram
          </Link>{' '}
          — and why it shouldn&rsquo;t be the only place you publish.
        </p>

        <h2 id="what-about-money">What about money?</h2>
        <p>
          This section is last on purpose. Most travel blogs never earn
          meaningful money, and the ones that do earn it slowly, from an
          archive of posts that rank and refer — affiliate links, ads once
          traffic is real, sponsorships once a niche audience is real. If
          income is the goal, a travel blog is one of the slower paths to it.
          If the blog is the goal, the money is a nice bonus that occasionally
          grows into a living. The realistic breakdown, including how
          monetization works on TravelFeed, is in{' '}
          <Link href="/how-to-make-money-with-a-travel-blog">
            how to make money with a travel blog
          </Link>
          .
        </p>

        <h2 id="what-it-costs">What starting a travel blog actually costs</h2>
        <p>
          Year-one numbers as of July 2026, yearly billing, rounded — and the
          catch behind each price tag:
        </p>
        <table>
          <thead>
            <tr>
              <th>The route</th>
              <th>Year one</th>
              <th>The catch</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Free platforms (Blogger, WordPress.com free)</td>
              <td>$0</td>
              <td>Their subdomain, possibly their ads, hard to move later</td>
            </tr>
            <tr>
              <td>TravelFeed Starter</td>
              <td>$19</td>
              <td>
                Community membership — publishes on travelfeed.com, not your
                own site
              </td>
            </tr>
            <tr>
              <td>TravelFeed Pro (your own blog + domain)</td>
              <td>$150 + a $10–15 domain</td>
              <td>
                We&rsquo;re the biased row — 50% new-member discount applies
                automatically at checkout; renews at $300/yr (Basic: $180/yr)
              </td>
            </tr>
            <tr>
              <td>Squarespace / Wix</td>
              <td>$190–205</td>
              <td>
                General-purpose builder — maps, trips and travel readers are
                your problem
              </td>
            </tr>
            <tr>
              <td>WordPress.com Personal</td>
              <td>$48</td>
              <td>
                Cheapest own-domain route — travel features are plugins you
                pick and configure yourself
              </td>
            </tr>
            <tr>
              <td>Self-hosted WordPress</td>
              <td>$150–180 at intro prices</td>
              <td>
                Intro deals often prepay 2–4 years; renewals land two to
                five times higher — and you&rsquo;re the sysadmin
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          That&rsquo;s the summary; the line-by-line version — renewal
          mechanics, the costs you can skip, and what the hours are really
          worth — is in{' '}
          <Link href="/blog/how-much-does-it-cost-to-start-a-travel-blog">
            what a travel blog really costs
          </Link>
          . And if you&rsquo;d rather get oriented before you build anything,
          start with{' '}
          <Link href="/blog/travel-blogging-for-beginners">
            travel blogging for beginners
          </Link>{' '}
          — the expectations, the vocabulary and the first 90 days, walkthrough
          not included.
        </p>

        <h2 id="faq">Frequently asked questions</h2>
        {FAQ.map(entry => (
          <div key={entry.questionName}>
            <h3>{entry.questionName}</h3>
            <p>{entry.acceptedAnswerText}</p>
          </div>
        ))}
      </ArticleBody>

      {/* Closing CTA — journal card, not the legacy highlights strip */}
      <section className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-16 tw-pt-8">
        <RouteFlourish className="tw-pb-10" />
        <div
          className={`${PAPER_CARD} tw-rounded-2xl tw-p-7 tw-text-center sm:tw-p-10`}
        >
          <p
            className={`${fraunces.className} ${INK} tw-mb-3 tw-mt-0 tw-text-2xl tw-font-semibold tw-tracking-tight sm:tw-text-3xl`}
          >
            Ready when you are.
          </p>
          <p className={`${MUTED} tw-mx-auto tw-mb-7 tw-mt-0 tw-max-w-lg`}>
            If TravelFeed sounded like your route, this is where it starts:
            your own travel blog, live in minutes, readers from day one. If it
            didn&rsquo;t, bookmark the comparison and go write — the world
            needs more real travel blogs either way.
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">
            <CtaButton onClick={() => setJoinOpen(true)}>
              Start your travel blog
              <ArrowIcon />
            </CtaButton>
            <GhostButton href="/tools/travel-blog-name-ideas-generator">
              Find your name first
            </GhostButton>
          </div>
        </div>
      </section>

      {joinOpen && (
        <LoginButton
          open
          hideButtons
          onClickClose={() => setJoinOpen(false)}
        />
      )}
      <Footer isNoSpace />
    </>
  );
}
